
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useSecurityAudit } from '@/hooks/useSecurityAudit';

export const useSecureVote = () => {
  const queryClient = useQueryClient();
  const { logSecurityEvent } = useSecurityAudit();

  return useMutation({
    mutationFn: async ({ 
      serverId, 
      playerName, 
      captchaToken, 
      ipHash,
      ipHashAlt,
      realIP,
      realIPAlt,
    }: { 
      serverId: string; 
      playerName: string; 
      captchaToken: string;
      ipHash: string;
      ipHashAlt?: string | null;
      realIP?: string | null;
      realIPAlt?: string | null;
    }) => {
      console.log('🔒 Starting secure vote with database function...');
      console.log('Vote data:', { 
        serverId, 
        playerName, 
        hasToken: !!captchaToken, 
        hasIpHash: !!ipHash,
        hasIpHashAlt: !!ipHashAlt,
        hasRealIP: !!realIP,
        hasRealIPAlt: !!realIPAlt,
      });

      try {
        // Use the secure database function (now dual-stack aware)
        const { data, error } = await supabase.rpc('cast_secure_vote', {
          p_server_id: serverId,
          p_player_name: playerName.trim(),
          p_ip_hash: ipHash,
          p_ip_hash_alt: ipHashAlt ?? null,
          p_real_ip: realIP ?? null,
          p_real_ip_alt: realIPAlt ?? null,
        } as any);

        console.log('📡 Database function response:', { data, error });
        
        // CORRECTION: Vérifier si supabase retourne une erreur
        if (error) {
          console.error('❌ Supabase RPC error:', error);
          throw new Error(error.message || 'Database error');
        }

        if (data && typeof data === 'object' && 'error' in data) {
          const errorMessage = (data as any).error;
          console.error('❌ Vote rejected by function:', errorMessage);
          
          if (errorMessage === 'VOTE_COOLDOWN_USER' || errorMessage === 'VOTE_COOLDOWN_IP') {
            throw new Error('VOTE_COOLDOWN');
          }
          
          throw new Error(errorMessage);
        }

        console.log('✅ Secure vote successful:', data);

        // Webhooks are dispatched server-side by the secure-vote edge function
        // after the vote is inserted. No client-side webhook trigger needed.

        return { success: true, message: "Vote enregistré avec succès !" };

      } catch (networkError) {
        console.error('❌ Database function error:', networkError);
        throw networkError;
      }
    },
    onSuccess: (data) => {
      console.log('🎉 Vote mutation successful:', data);
      queryClient.invalidateQueries({ queryKey: ['servers'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
      
      // Logger le vote réussi
      logSecurityEvent({
        eventType: 'vote_success',
        eventData: {
          message: 'Vote enregistré avec succès'
        }
      });
      
      toast({
        title: "✅ Vote enregistré avec succès !",
        description: data.message || "Merci pour votre vote !",
      });

      // Actualiser la page après un vote réussi
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    },
    onError: (error: any) => {
      console.error('❌ Secure vote mutation error:', error);
      
      const errorMessage = error.message || 'Unknown error';
      
      // Logger l'erreur de vote
      logSecurityEvent({
        eventType: errorMessage.includes('COOLDOWN') ? 'vote_cooldown_violation' : 'vote_error',
        eventData: {
          error: errorMessage,
          details: error.toString()
        }
      });
      
      if (errorMessage === 'VOTE_COOLDOWN') {
        toast({
          title: "⏰ Vous avez déjà voté !",
          description: "Vous devez attendre 2 heures entre chaque vote.",
          variant: "destructive",
        });
      } else if (errorMessage === 'RATE_LIMITED') {
        toast({
          title: "🚫 Trop de tentatives",
          description: "Vous avez effectué trop de votes récemment. Réessayez plus tard.",
          variant: "destructive",
        });
      } else if (errorMessage === 'CAPTCHA_INVALID') {
        toast({
          title: "🤖 Vérification échouée",
          description: "La vérification de sécurité a échoué. Veuillez réessayer.",
          variant: "destructive",
        });
      } else if (errorMessage === 'SERVER_NOT_FOUND') {
        toast({
          title: "❌ Serveur introuvable",
          description: "Ce serveur n'est pas disponible pour le vote.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "❌ Erreur de vote",
          description: "Une erreur est survenue lors du vote. Veuillez réessayer.",
          variant: "destructive",
        });
      }

      // Log the error for security monitoring
      logSecurityEvent({
        eventType: 'vote_error_frontend',
        eventData: { error: errorMessage }
      });
    },
  });
};
